import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ShieldAlert, HardHat, Siren, FileText, ClipboardList, Building2, Send } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { RevealText } from "@/components/shared/RevealText";
import { SectionReveal, StaggerGroup, StaggerItem } from "@/components/shared/SectionReveal";
import { IMAGES } from "@/data/images";
import apiClient from "@/lib/api";

const ICONS = [ShieldAlert, HardHat, Siren, FileText, ClipboardList, Building2];

export default function Pro() {
  const { t } = useTranslation();
  const features = t("pro.features", { returnObjects: true });
  const audience = t("pro.audience_items", { returnObjects: true });
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post("/leads/pro", form);
      setSent(true);
      toast.success(t("pro.form_success"));
    } catch {
      toast.error(t("common.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="pro-page">
      <SEO title={`${t("pro.hero_title")} — Fastority PRO`} description={t("pro.hero_subtitle")} path="pro" />

      <section className="relative bg-slate900 text-white overflow-hidden">
        <img src={IMAGES.b2bProfessional} alt="Fastority PRO" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate900 via-slate900/95 to-slate900/60" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-28 sm:py-36">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] bg-white/10 border border-white/15 rounded-full px-4 py-2 mb-8" data-testid="pro-badge">
            {t("pro.badge")}
          </span>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-3xl">
            <RevealText lines={[t("pro.hero_title")]} />
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-xl">{t("pro.hero_subtitle")}</p>
          <a href="#pro-form" data-testid="pro-hero-cta" className="mt-8 inline-flex items-center gap-2 bg-coral text-white font-semibold px-7 py-4 rounded-full hover:scale-[1.02] transition-transform duration-300">
            {t("pro.hero_cta")}
          </a>
        </div>
      </section>

      <section className="py-20 bg-deepblue text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <SectionReveal className="mb-10">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">{t("pro.audience_title")}</h2>
          </SectionReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {audience.map((a, i) => (
              <SectionReveal key={a} delay={i * 0.06}>
                <div data-testid={`pro-audience-${i}`} className="bg-white/10 border border-white/15 rounded-2xl p-5 text-sm font-medium h-full">
                  {a}
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-28 bg-offwhite">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionReveal className="max-w-2xl mb-14">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-slate900">{t("pro.features_title")}</h2>
          </SectionReveal>
          <StaggerGroup className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <StaggerItem key={f.title}>
                  <div data-testid={`pro-feature-${i}`} className="bg-white rounded-3xl p-8 h-full shadow-[0_8px_30px_rgba(30,58,138,0.08)] border border-gray-100">
                    <div className="w-12 h-12 rounded-2xl bg-deepblue flex items-center justify-center mb-6">
                      <Icon className="text-coral" size={22} strokeWidth={2.2} />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-slate900 mb-2">{f.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      <section id="pro-form" className="py-24 sm:py-28 bg-white">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          <SectionReveal className="text-center mb-10">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-slate900">{t("pro.form_title")}</h2>
            <p className="mt-3 text-slate-600">{t("pro.form_subtitle")}</p>
          </SectionReveal>

          {sent ? (
            <SectionReveal className="text-center bg-emeraldgreen/10 border border-emeraldgreen/30 rounded-3xl p-10" >
              <CheckCircle2 className="mx-auto mb-3 text-emeraldgreen" size={36} />
              <p className="font-heading font-bold text-lg text-slate900" data-testid="pro-form-success">{t("pro.form_success")}</p>
            </SectionReveal>
          ) : (
            <form onSubmit={submit} data-testid="pro-lead-form" className="bg-offwhite rounded-3xl p-8 border border-gray-100 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required data-testid="pro-form-name" placeholder={t("common.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
                <input required type="email" data-testid="pro-form-email" placeholder={t("common.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required data-testid="pro-form-company" placeholder={t("common.company")} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
                <input data-testid="pro-form-role" placeholder={t("common.role")} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
              </div>
              <input data-testid="pro-form-phone" placeholder={t("common.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
              <textarea data-testid="pro-form-message" placeholder={t("common.message")} rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
              <button type="submit" disabled={loading} data-testid="pro-form-submit" className="w-full inline-flex items-center justify-center gap-2 bg-coral text-white font-semibold px-6 py-4 rounded-full hover:scale-[1.01] transition-transform duration-300 disabled:opacity-60">
                {loading ? t("common.sending") : t("pro.form_submit")} <Send size={16} />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
